import { SelectOption } from '@/components/types';
import {
  EnrichedHumanDocument,
  EnrichedMatterContact,
  EnrichedOrganizationDocument,
  FlattenedMatter,
} from '@/database/aggregateTypes';
import { assertUnreachable } from '@/utils/assertUnreachable';
import { FirmData, InsuranceProvider, MatterType } from '@shared/types';
import {
  AddEditMatterFormData,
  CommonFormDataFields,
  ContingencyFormData,
  FeeType,
  FlatFeeFormData,
  FormInsuranceCase,
  HourlyMatterFormData,
  HumanContact,
  InsuranceFormData,
  OrganizationContact,
  UnknownFeeTypeFormData,
} from './types';

export const makeFormData = (props: {
  matter: FlattenedMatter | undefined;
  initialOverrides?: Partial<AddEditMatterFormData>;
  leadSourceOptions: SelectOption[];
  practiceAreaOptions: SelectOption[];
  firm: FirmData | undefined;
}): AddEditMatterFormData => {
  const {
    matter,
    initialOverrides,
    leadSourceOptions,
    practiceAreaOptions,
    firm,
  } = props;

  const { clients, adverseParties } = convertAndCategorizeContacts(
    matter?.contacts ?? [],
    matter?.organizationRepresentatives ?? {},
  );

  const commonFields: CommonFormDataFields = {
    matterName: matter?.name ?? null,
    description: matter?.description ?? null,
    practiceAreaId: (() => {
      const practiceAreaName = (matter as any)?.practiceArea;
      if (!practiceAreaName) return practiceAreaOptions[0]?.value ?? null;
      // Try to find by ID first (new format)
      const foundById = practiceAreaOptions.find(
        (option) => option.value === practiceAreaName,
      );
      if (foundById) return foundById.value;
      // Try to find by name (legacy format)
      const foundByName = practiceAreaOptions.find(
        (option) => option.label === practiceAreaName,
      );
      return foundByName?.value ?? practiceAreaOptions[0]?.value ?? null;
    })(),
    clients,
    adverseParties,
    primaryClientTempId: matter?.primaryClient?.id ?? null,
    primaryAdversePartyTempId: matter?.primaryAdverseParty?.id ?? null,
    leadSourceId: matter?.leadSource?.id ?? leadSourceOptions[0]?.value ?? null,
    matterStatus: matter?.status ?? 'new',
    matterSubStatusId: matter?.subStatusId ?? null,
    responsibleAttorneyId: matter?.responsibleAttorney?.id ?? null,
    otherAssigneeId: (matter as any)?.otherAssignee?.id ?? null,
    scope: matter?.scope ?? null,
    state: matter?.state ?? firm?.headquartersUsState ?? 'Colorado',
  };

  const composeFormData = (
    feeTypeFields:
      | InsuranceFormData
      | HourlyMatterFormData
      | FlatFeeFormData
      | ContingencyFormData
      | UnknownFeeTypeFormData,
  ): AddEditMatterFormData => {
    return {
      ...commonFields,
      ...feeTypeFields,
      ...initialOverrides,
    } as AddEditMatterFormData;
  };

  const feeType = feeTypeForMatterType(matter?.matterType);
  switch (feeType) {
    case 'Insurance': {
      const insuranceCases: FormInsuranceCase[] =
        matter?.insuranceCases?.map((insuranceCase) => ({
          tempId: insuranceCase.id,
          firestoreId: insuranceCase.id,
          provider: insuranceCase.provider,
          caseId: insuranceCase.caseId ?? null,
          memberId: insuranceCase.memberId ?? null,
          caseValue: insuranceCase.caseValue?.toString() ?? null,
          caseType: insuranceCase.caseType ?? null,
          otherInsuranceProviderName:
            insuranceCase.otherInsuranceProviderName ?? null,
        })) ?? [];

      return composeFormData({
        feeType,
        insuranceProvider: insuranceProviderForMatterType(matter?.matterType),
        insuranceCases,
      });
    }
    case 'Hourly': {
      return composeFormData({
        feeType,
        retainer: matter?.retainer ?? null,
        responsibleAttyHourlyRate: matter?.responsibleAttyHourlyRate ?? null,
        referralOriginatorId: matter?.referralOriginator?.id ?? null,
        engagementOriginatorId: matter?.engagementOriginator?.id ?? null,
      });
    }
    case 'FlatFee': {
      return composeFormData({
        feeType,
        flatFeeAmt: matter?.flatFeeAmt ?? null,
        referralOriginatorId: matter?.referralOriginator?.id ?? null,
        engagementOriginatorId: matter?.engagementOriginator?.id ?? null,
      });
    }
    case 'Contingency': {
      return composeFormData({
        feeType,
        contingencyFees: {
          pctPreFilingOfLawsuit:
            matter?.contingencyFees?.pctPreFilingOfLawsuit ?? null,
          pctUponFilingOfLawsuit:
            matter?.contingencyFees?.pctUponFilingOfLawsuit ?? null,
          pctUponCommencementOfTrial:
            matter?.contingencyFees?.pctUponCommencementOfTrial ?? null,
        },
        referralOriginatorId: matter?.referralOriginator?.id ?? null,
        engagementOriginatorId: matter?.engagementOriginator?.id ?? null,
      });
    }
    case `Unknown`: {
      return composeFormData({
        feeType: 'Unknown',
        referralOriginatorId: matter?.referralOriginator?.id ?? null,
        engagementOriginatorId: matter?.engagementOriginator?.id ?? null,
      });
    }
    default: {
      assertUnreachable(feeType);
      throw new Error(`Unexpected fee type: ${feeType}`);
    }
  }
};

const feeTypeForMatterType = (matterType: MatterType | undefined): FeeType => {
  if (!matterType) return 'Unknown';
  switch (matterType) {
    case 'ARAG':
    case 'MetLife':
    case 'LegalEase':
    case 'OtherInsurance':
      return 'Insurance';
    case 'Hourly':
      return 'Hourly';
    case 'FlatFee':
      return 'FlatFee';
    case 'Contingency':
      return 'Contingency';
    case 'Pro Bono':
      return 'Unknown';
    case 'Subscription':
      return 'Unknown';
    case `Don't Know`:
      return 'Unknown';
    default:
      assertUnreachable(matterType);
      return 'Unknown';
  }
};

const insuranceProviderForMatterType = (
  matterType: MatterType | undefined,
): InsuranceProvider => {
  switch (matterType) {
    case 'ARAG':
      return 'arag';
    case 'MetLife':
      return 'metlife';
    case 'LegalEase':
      return 'legalease';
    case 'OtherInsurance':
      return 'other';
    default:
      return 'other';
  }
};

const convertAndCategorizeContacts = (
  contacts: EnrichedMatterContact[],
  organizationRepresentatives: Record<string, string[]>,
) => {
  let formContacts = convertContacts(contacts);

  // Move representatives from the top-level collection of humans to the
  // appropriate organization's `representatives` field.
  for (const organizationId in organizationRepresentatives) {
    const orgContact = formContacts.find(
      (contact) =>
        contact.firestoreId === organizationId &&
        contact.type === 'organization',
    );
    if (!orgContact || orgContact.type !== 'organization') {
      continue;
    }

    const { orgRepresentatives, updatedFormContacts } = formContacts.reduce(
      (acc, client) => {
        if (
          client.type === 'human' &&
          client.role === orgContact.role &&
          organizationRepresentatives[organizationId].includes(client.tempId)
        ) {
          acc.orgRepresentatives.push(client);
        } else {
          acc.updatedFormContacts.push(client);
        }
        return acc;
      },
      {
        orgRepresentatives: [] as HumanContact[],
        updatedFormContacts: [] as (HumanContact | OrganizationContact)[],
      },
    );
    orgContact.representatives = orgRepresentatives;
    formContacts = updatedFormContacts;
  }
  return formContacts.reduce(
    (acc, contact) => {
      switch (contact.role) {
        case 'client':
          acc.clients.push(contact);
          break;
        case 'adverseParty':
          acc.adverseParties.push(contact);
          break;
        default:
          assertUnreachable(contact.role);
      }
      return acc;
    },
    {
      clients: [] as (HumanContact | OrganizationContact)[],
      adverseParties: [] as (HumanContact | OrganizationContact)[],
    },
  );
};

const convertContacts = (contacts: EnrichedMatterContact[]) => {
  return contacts.map((contact) =>
    contact.type === 'human'
      ? convertHumanToFormContact(contact)
      : convertOrganizationToFormContact(contact),
  );
};

const convertHumanToFormContact = (
  contact: EnrichedHumanDocument,
): HumanContact => {
  return {
    tempId: contact.id,
    firestoreId: contact.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    phoneNumbers: contact.phoneNumbers ?? [],
    primaryPhoneNumberId: contact.primaryPhoneNumberId ?? null,
    emailAddresses: contact.emailAddresses ?? [],
    primaryEmailAddressId: contact.primaryEmailAddressId ?? null,
    preferredLanguage: contact.preferredLanguage ?? null,
    role: contact.role,
    type: 'human',
  };
};

const convertOrganizationToFormContact = (
  contact: EnrichedOrganizationDocument,
): OrganizationContact => {
  return {
    tempId: contact.id,
    companyName: contact.companyName,
    firestoreId: contact.id,
    role: contact.role,
    type: 'organization',
    phoneNumbers: contact.phoneNumbers ?? [],
    primaryPhoneNumberId: contact.primaryPhoneNumberId ?? null,
    emailAddresses: contact.emailAddresses ?? [],
    primaryEmailAddressId: contact.primaryEmailAddressId ?? null,
    representatives: [],
  };
};
